import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FeatureRequestService } from '@/services/featureRequestService';
import { FeatureRequest } from '@/types/featureRequestTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ThumbsUp, Lightbulb, CheckCircle, Clock, RotateCcw, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/lib/utils';

const FeatureRequests: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const {
    data: featureRequests = [],
    isLoading
  } = useQuery({
    queryKey: ['featureRequests'],
    queryFn: () => user ? FeatureRequestService.getFeatureRequestsWithVoteStatus(user.id) : [],
    enabled: !!user
  });

  const createMutation = useMutation({
    mutationFn: ({
      title,
      description,
      created_by,
      organization_id
    }: {
      title: string;
      description: string;
      created_by: string;
      organization_id: string;
    }) => FeatureRequestService.createFeatureRequest({
      title,
      description,
      created_by,
      organization_id
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['featureRequests']
      });
      setIsDialogOpen(false);
      resetForm();
    }
  });

  const voteMutation = useMutation({
    mutationFn: ({
      id
    }: {
      id: string;
    }) => {
      if (!user) return Promise.reject('User not authenticated');
      return FeatureRequestService.voteForFeatureRequest(id, user.id, user.user_metadata?.organization_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['featureRequests']
      });
    }
  });

  const removeVoteMutation = useMutation({
    mutationFn: ({
      id
    }: {
      id: string;
    }) => {
      if (!user) return Promise.reject('User not authenticated');
      return FeatureRequestService.removeVote(id, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['featureRequests']
      });
    }
  });

  const resetForm = () => {
    setTitle('');
    setDescription('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    createMutation.mutate({
      title,
      description,
      created_by: user.id,
      organization_id: user.user_metadata?.organization_id
    });
  };

  const handleVote = (id: string, hasVoted: boolean) => {
    if (hasVoted) {
      removeVoteMutation.mutate({
        id
      });
    } else {
      voteMutation.mutate({
        id
      });
    }
  };

  const getStatusBadge = (status: FeatureRequest['status']) => {
    switch (status) {
      case 'planned':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          <Clock className="h-3 w-3 mr-1" />
          Planned
        </Badge>;
      case 'in-progress':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          <RotateCcw className="h-3 w-3 mr-1" />
          In Progress
        </Badge>;
      case 'implemented':
        return <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Implemented
        </Badge>;
      default:
        return null;
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All Requests' },
    { value: 'my-votes', label: 'My Votes' },
    { value: 'uncategorized', label: 'Uncategorized' },
    { value: 'planned', label: 'Planned' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'implemented', label: 'Implemented' }
  ];

  const filteredRequests = featureRequests.filter(req => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'my-votes') return req.has_voted;
    if (activeFilter === 'uncategorized') return req.status === 'uncategorized';
    if (activeFilter === 'planned') return req.status === 'planned';
    if (activeFilter === 'in-progress') return req.status === 'in-progress';
    if (activeFilter === 'implemented') return req.status === 'implemented';
    return true;
  });

  return (
    <Card className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-left">Feature Requests</CardTitle>
          <CardDescription>
            Submit feature ideas or vote on existing requests
          </CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Lightbulb className="h-4 w-4 mr-2" />
              Submit Feature Idea
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>Submit a Feature Request</DialogTitle>
                <DialogDescription>
                  Share your ideas for new features or improvements
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="title" className="text-sm font-medium">Feature Title</label>
                  <Input id="title" placeholder="Brief title for your feature idea" value={title} onChange={e => setTitle(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="description" className="text-sm font-medium">Description</label>
                  <Textarea id="description" placeholder="Describe the feature and how it would help you" rows={5} value={description} onChange={e => setDescription(e.target.value)} required />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent>
        <div className="mb-6">
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter requests" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="flex items-center gap-2">
                    {option.value === 'all' && <Filter className="h-4 w-4" />}
                    {option.value === 'my-votes' && <ThumbsUp className="h-4 w-4" />}
                    {option.value === 'uncategorized' && <Lightbulb className="h-4 w-4" />}
                    {option.value === 'planned' && <Clock className="h-4 w-4" />}
                    {option.value === 'in-progress' && <RotateCcw className="h-4 w-4" />}
                    {option.value === 'implemented' && <CheckCircle className="h-4 w-4" />}
                    {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Lightbulb className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2">No feature requests found in this category</p>
              {activeFilter === 'all' && (
                <Button className="mt-4" onClick={() => setIsDialogOpen(true)}>
                  Submit the first feature request
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map(request => (
                <Card key={request.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{request.title}</CardTitle>
                        <CardDescription className="text-xs">
                          Requested on {formatDate(request.created_at)}
                        </CardDescription>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <p className="text-sm">{request.description}</p>
                  </CardContent>
                  <CardFooter className="bg-gray-50 flex justify-between pt-2 pb-2">
                    <Button variant={request.has_voted ? "default" : "outline"} size="sm" onClick={() => handleVote(request.id, !!request.has_voted)} disabled={voteMutation.isPending || removeVoteMutation.isPending}>
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      {request.has_voted ? 'Voted' : 'Vote'}
                      <span className="ml-1 text-xs">({request.votes_count})</span>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeatureRequests;
